import {ErrorMessage, Field, Form, Formik} from "formik";
import postData from "../services/postData";
import Modal from "./Modal";
import {useState, useRef} from "react";
import {ScrollToFieldError} from "./formik/ScrollToFieldError";
import {formSchema} from "../schema/formSchema";
import ConsentCheckboxes from "./ConsentCheckboxes";
import {dealers} from "../data/dealers";

// Funkcja do mapowania kodu KVPS na dealer ID
// KVPS format: "20270C" → Dealer ID: "20270" (bez przedrostka)
const getGlobalIdFromKVPS = (kvpsCode) => {
    if (!kvpsCode) return '';
    
    // Usuń "C" z końca kodu KVPS - zwracamy sam numer
    const numericCode = kvpsCode.replace(/C$/, '');
    
    // Zwracamy czysty numer bez przedrostka "456-"
    return numericCode;
};

export default function ServiceForm() {

    const [successModal, setSuccessModal] = useState(false);
    const [failureModal, setFailureModal] = useState(false);
    const [formIsSubmitting, setFormIsSubmitting] = useState(false);
    const formikRef = useRef(null);

    const handleSuccessModalClose = () => {
        setSuccessModal(false);
        window.location.href = 'https://www.skoda.pl/auta-uzywane/skoda-plus-certyfikowane-uzywane';
    };

    const dataLayerPush = (orderId, leadId) => {
        try {
            window.dataLayer.push({
                'event': 'formularze',
                'wypelniony-formularz': 'trade-in-zapisano',
                'orderid': orderId,
                'leadid': leadId
            });
            console.log('dataLayer.push ok:', orderId);
        } catch (err) {
            console.error('dataLayer.push error:', err);
        }
    };

    const syneriseSendFormData = (orderId, values) => {
        if (!('SR' in window)) {
            console.error('SR undefined');
            return;
        }

        // Pobierz GlobalID dla wybranego dealera
        const globalId = getGlobalIdFromKVPS(values.dealer_id);

        const dynamicFormType = `trade-in,${values.tradein_brand},${values.tradein_model},bp`;
        const snrData = {
            orderID: orderId,
            name: values.name,
            email: values.email,
            phone: values.phone,
            tradein_brand: values.tradein_brand,
            tradein_model: values.tradein_model,
            dealer_id: globalId, // Używaj GlobalID zamiast kodu KVPS
        };
        
        try {
            window.SR.event.sendFormData(dynamicFormType, snrData)
                .then((res) => {
                    console.log('Synerise success:', dynamicFormType);
                    console.log(res);
                })
                .catch((err) => {
                    console.log('Synerise error:', dynamicFormType);
                    console.error(err);
                });
        } catch (err) {
            console.error('SR error', err);
        }
    };

    const insertSyneriseScript = () => {
        const syneriseScript = document.createElement('script');
        syneriseScript.innerHTML = `
            function onSyneriseLoad() {
                SR.init({
                    'trackerKey':'F79A2C6D-7D1D-2E3A-F1D4-997A40970066',
                });
            }

            (function(s,y,n,e,r,i,se){s['SyneriseObjectNamespace']=r;s[r]=s[r]||[],s[r]._t=1*new Date(),
                s[r]._i=0,s[r]._l=i;var z=y.createElement(n),se=y.getElementsByTagName(n)[0];z.async=1;z.src=e;
                se.parentNode.insertBefore(z,se);z.onload=z.onreadystatechange=function(){var rdy=z.readyState;
                    if(!rdy||/complete|loaded/.test(z.readyState)){s[i]();z.onload = null;z.onreadystatechange=null;}};
            })(window,document,'script','//www.snrcdn.net/sdk/3.0/synerise-javascript-sdk.min.js','SR', 'onSyneriseLoad');
        `;
        document.body.appendChild(syneriseScript);
    };

    const onSubmit = (values, {setSubmitting}) => {
        console.log('Form onSubmit called with values:', values);
        setSubmitting(false);
        setFormIsSubmitting(true);

        console.log('Checking window.SR:', window.SR);
        if(window.SR === undefined) {
            console.log('Inserting Synerise script...');
            insertSyneriseScript();
        }

        // Znajdź dealera na podstawie ID
        const selectedDealer = dealers.find(d => d.value === values.dealer_id);
        
        // Pobierz GlobalID dla wybranego dealera
        const globalId = getGlobalIdFromKVPS(values.dealer_id);

        // Przygotuj dane zgodnie z formatem oczekiwanym przez PHP
        const dataToSend = {
            // Dane kontaktowe
            name: values.name,
            email: values.email,
            phone: values.phone,
            
            // Dealer - wysyłaj GlobalID zamiast kodu KVPS
            dealer_id: globalId,
            dealerName: selectedDealer ? selectedDealer.label.split(' - ')[0] : '',
            dealerLocation: selectedDealer ? selectedDealer.label.split(' - ')[1] : '',
            
            // Dane pojazdu do oddania (trade-in)
            tradeinBrand: values.tradein_brand,
            tradeinModel: values.tradein_model,
            vin: values.vin,
            tradeinFirstRegistrationDate: values.tradein_first_registration_date,
            tradeinMileage: values.tradein_mileage,
            tradeinRegistrationNumber: values.tradein_registration_number,
            tradeinDescription: values.tradein_description,
            voivodeship: values.voivodeship,
        };
        
        console.log('Data to send:', dataToSend);

        const intervalId = setInterval(() => {
            console.log('Checking for window.SR...', window.SR);
            if ((window.SR !== undefined)) {
                console.log('window.SR found! Submitting form...');
                clearInterval(intervalId);
                postData(dataToSend)
                    .then((data) => {
                        console.log('Form submission successful:', data);
                        if (data && data.status === 'ok') {
                            dataLayerPush(data.orderId, data.leadId);
                            syneriseSendFormData(data.orderId, values);
                            setSuccessModal(true);
                        } else {
                            console.log('Form submission failed - invalid response:', data);
                            setFailureModal(true);
                        }
                    })
                    .catch((error) => {
                        console.error('Form submission error:', error);
                        setFailureModal(true);
                    })
                    .finally(() => {
                        setFormIsSubmitting(false);
                        window.scrollTo({
                            top: 0,
                            left: 0,
                            behavior: "smooth"
                        });
                    });
            }
        }, 300);

        // Set failure when no SR in window after 5s
        setTimeout(() => {
            clearInterval(intervalId);
            if (window.SR === undefined) {
                console.error('Synerise (window.SR) not loaded after 5 seconds - showing failure modal');
                setFailureModal(true);                    
                setFormIsSubmitting(false);
                window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: "smooth"
                });
                return;
            }
        }, 5000);
    };

    return (
        <>
            <div className="form-container">

                <Formik
                    initialValues={{
                        // Krok 1 - dane pojazdu
                        tradein_brand: '',
                        tradein_model: '',
                        vin: '',
                        tradein_first_registration_date: '',
                        tradein_mileage: '',
                        tradein_registration_number: '',
                        tradein_description: '',
                        voivodeship: '',
                        dealer_id: '',
                        // Krok 2 - dane kontaktowe
                        name: '',
                        email: '',
                        phone: '',
                        // Stan formularza
                        currentStep: 1
                    }}
                    validationSchema={formSchema}
                    onSubmit={onSubmit}
                >
                    {(formikProps) => {
                        formikRef.current = formikProps;
                        const {values, setFieldValue} = formikProps;
                        const currentStep = values.currentStep;
                        
                        const goToNextStep = () => {
                            setFieldValue('currentStep', 2);
                        };
                        
                        const goToPrevStep = () => {
                            setFieldValue('currentStep', 1);
                        };
                        
                        return (
                        <>
                        <h1 className="form-title">Zmień auto na nową Škodę – wygodnie i bez stresu</h1>
                        
                        <Form className="test-drive-form">
                            <ScrollToFieldError />
                            
                            {values.currentStep === 1 && (
                                <>
                                    <div className="gs_trade_in_2025__step_header">
                                        <div className="step-indicator">
                                            <div className="step active">
                                                <span className="step-number">1</span>
                                                <span className="step-label">Informacje o pojeździe</span>
                                            </div>
                                            <div className="step-divider"></div>
                                            <div className="step">
                                                <span className="step-number">2</span>
                                                <span className="step-label">Dane osobowe</span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="step-description">
                                        <p>Wypełnij formularz, a nasi partnerzy przygotują dla Ciebie bezpłatną <br className="desktop-only"/>i niezobowiązującą wycenę pojazdu.</p>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="tradein_brand">Podaj markę swojego samochodu:*</label>
                                            <Field
                                                type="text"
                                                id="tradein_brand"
                                                name="tradein_brand"
                                                placeholder="np. Škoda, Volkswagen, Audi..."
                                                maxLength="50"
                                            />
                                            <ErrorMessage name="tradein_brand" component="div" className="error-message" />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="tradein_model">Podaj model samochodu:*</label>
                                            <Field
                                                type="text"
                                                id="tradein_model"
                                                name="tradein_model"
                                                placeholder="np. Octavia, Superb, Fabia..."
                                                maxLength="50"
                                            />
                                            <ErrorMessage name="tradein_model" component="div" className="error-message" />
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="vin">Podaj numer VIN pojazdu:*</label>
                                            <Field
                                                type="text"
                                                id="vin"
                                                name="vin"
                                                placeholder="Podaj nr. VIN"
                                                maxLength="17"
                                                onChange={(e) => {
                                                    const upperValue = e.target.value.toUpperCase();
                                                    setFieldValue('vin', upperValue);
                                                }}
                                            />
                                            <ErrorMessage name="vin" component="div" className="error-message" />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="tradein_first_registration_date">Podaj datę pierwszej rejestracji pojazdu:*</label>
                                            <Field
                                                type="date"
                                                id="tradein_first_registration_date"
                                                name="tradein_first_registration_date"
                                            />
                                            <ErrorMessage name="tradein_first_registration_date" component="div" className="error-message" />
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="tradein_mileage">Podaj przebieg pojazdu:*</label>
                                            <Field
                                                as="select"
                                                id="tradein_mileage"
                                                name="tradein_mileage"
                                            >
                                                <option value="">Wybierz przebieg</option>
                                                <option value="0 - 10 000">0 - 10 000</option>
                                                <option value="10 001 - 20 000">10 001 - 20 000</option>
                                                <option value="20 001 - 30 000">20 001 - 30 000</option>
                                                <option value="30 001 - 40 000">30 001 - 40 000</option>
                                                <option value="40 001 - 50 000">40 001 - 50 000</option>
                                                <option value="50 001 - 60 000">50 001 - 60 000</option>
                                                <option value="60 001 - 70 000">60 001 - 70 000</option>
                                                <option value="70 001 - 80 000">70 001 - 80 000</option>
                                                <option value="80 001 - 90 000">80 001 - 90 000</option>
                                                <option value="90 001 - 100 000">90 001 - 100 000</option>
                                                <option value="100 001 - 110 000">100 001 - 110 000</option>
                                                <option value="110 001 - 120 000">110 001 - 120 000</option>
                                                <option value="120 001 - 130 000">120 001 - 130 000</option>
                                                <option value="130 001 - 140 000">130 001 - 140 000</option>
                                                <option value="140 001 - 150 000">140 001 - 150 000</option>
                                                <option value="150 001 - 160 000">150 001 - 160 000</option>
                                                <option value="160 001 - 170 000">160 001 - 170 000</option>
                                                <option value="170 001 - 180 000">170 001 - 180 000</option>
                                                <option value="180 001 - 190 000">180 001 - 190 000</option>
                                                <option value="190 001 - 200 000">190 001 - 200 000</option>
                                                <option value="powyżej 200 000">powyżej 200 000</option>
                                            </Field>
                                            <ErrorMessage name="tradein_mileage" component="div" className="error-message" />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="tradein_registration_number">Podaj numer rejestracyjny pojazdu:*</label>
                                            <Field
                                                type="text"
                                                id="tradein_registration_number"
                                                name="tradein_registration_number"
                                                placeholder="Podaj nr rejestracyjny"
                                                maxLength="10"
                                                onChange={(e) => {
                                                    const upperValue = e.target.value.toUpperCase();
                                                    setFieldValue('tradein_registration_number', upperValue);
                                                }}
                                            />
                                            <ErrorMessage name="tradein_registration_number" component="div" className="error-message" />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="tradein_description">Jeśli chcesz zwiększyć prawdopodobieństwo odkupu, dodaj krótki opis swojego samochodu:*</label>
                                        <Field
                                            as="textarea"
                                            id="tradein_description"
                                            name="tradein_description"
                                            placeholder="Opisz stan pojazdu, wyposażenie, historię serwisową..."
                                            maxLength="500"
                                            rows="4"
                                        />
                                        <ErrorMessage name="tradein_description" component="div" className="error-message" />
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="voivodeship">Wybierz województwo:</label>
                                            <Field
                                                as="select"
                                                id="voivodeship"
                                                name="voivodeship"                                                onChange={(e) => {
                                                    setFieldValue('voivodeship', e.target.value);
                                                    setFieldValue('dealer_id', '');
                                                }}                                            >
                                                <option value="">Województwo</option>
                                                <option value="dolnoslaskie">dolnośląskie</option>
                                                <option value="kujawsko-pomorskie">kujawsko-pomorskie</option>
                                                <option value="lubelskie">lubelskie</option>
                                                <option value="lubuskie">lubuskie</option>
                                                <option value="lodzkie">łódzkie</option>
                                                <option value="malopolskie">małopolskie</option>
                                                <option value="mazowieckie">mazowieckie</option>
                                                <option value="opolskie">opolskie</option>
                                                <option value="podkarpackie">podkarpackie</option>
                                                <option value="podlaskie">podlaskie</option>
                                                <option value="pomorskie">pomorskie</option>
                                                <option value="slaskie">śląskie</option>
                                                <option value="swietokrzyskie">świętokrzyskie</option>
                                                <option value="warminsko-mazurskie">warmińsko-mazurskie</option>
                                                <option value="wielkopolskie">wielkopolskie</option>
                                                <option value="zachodniopomorskie">zachodniopomorskie</option>
                                            </Field>
                                            <ErrorMessage name="voivodeship" component="div" className="error-message" />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="dealer_id">Wybierz dealera:*</label>
                                            <Field
                                                as="select"
                                                id="dealer_id"
                                                name="dealer_id"
                                            >
                                                {dealers
                                                    .filter(dealer => dealer.value === "" || !values.voivodeship || values.voivodeship === 'podlaskie' || dealer.voivodeship === values.voivodeship)
                                                    .map((dealer) => (
                                                        <option key={dealer.value} value={dealer.value}>
                                                            {dealer.label}
                                                        </option>
                                                    ))}
                                            </Field>
                                            <ErrorMessage name="dealer_id" component="div" className="error-message" />
                                        </div>
                                    </div>

                                    <div className="button-wrapper">
                                        <button
                                            type="button"
                                            className="gs_trade_in_2025__btn gs_trade_in_2025__btn--next"
                                            onClick={goToNextStep}
                                            disabled={!values.tradein_brand || !values.tradein_model || !values.vin || !values.tradein_first_registration_date || !values.tradein_mileage || !values.tradein_registration_number || !values.dealer_id}
                                        >
                                            Dalej
                                        </button>
                                    </div>
                                </>
                            )}

                            {values.currentStep === 2 && (
                                <>
                                    <div className="gs_trade_in_2025__step_header">
                                        <div className="step-indicator">
                                            <div className="step completed">
                                                <span className="step-number">1</span>
                                                <span className="step-label">Informacje o pojeździe</span>
                                            </div>
                                            <div className="step-divider"></div>
                                            <div className="step active">
                                                <span className="step-number">2</span>
                                                <span className="step-label">Dane osobowe</span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="step-description">
                                        <p>Wypełnij formularz, a nasi partnerzy przygotują dla Ciebie bezpłatną <br className="desktop-only"/>i niezobowiązującą wycenę pojazdu.</p>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="name">Imię i nazwisko*</label>
                                        <Field
                                            type="text"
                                            id="name"
                                            name="name"
                                            placeholder="Podaj swoje imię i nazwisko"
                                            maxLength="100"
                                        />
                                        <ErrorMessage name="name" component="div" className="error-message" />
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="phone">Telefon*</label>
                                            <Field
                                                type="tel"
                                                id="phone"
                                                name="phone"
                                                placeholder="Wpisz 9-cyfrowy numer"
                                                maxLength="9"
                                            />
                                            <ErrorMessage name="phone" component="div" className="error-message" />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="email">Email*</label>
                                            <Field
                                                type="email"
                                                id="email"
                                                name="email"
                                                placeholder="np. jan.kowalski@gmail.com"
                                                maxLength="100"
                                            />
                                            <ErrorMessage name="email" component="div" className="error-message" />
                                        </div>
                                    </div>
                                </>
                            )}
                        </Form>

                        {currentStep === 2 && (
                            <div className="form-buttons">
                                <button
                                    type="button"
                                    className="gs_trade_in_2025__btn gs_trade_in_2025__btn--back"
                                    onClick={goToPrevStep}
                                >
                                    Wstecz
                                </button>
                                
                                <button
                                    type="button"
                                    className="gs_trade_in_2025__btn"
                                    disabled={formIsSubmitting}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (formikRef.current) {
                                            formikRef.current.handleSubmit();
                                        }
                                    }}
                                >
                                    {formIsSubmitting ? 'Wysyłanie...' : 'Wyślij zgłoszenie'}
                                </button>
                            </div>
                        )}

                        <ConsentCheckboxes />
                        </>
                        );
                    }}
                </Formik>
            </div>

            <Modal open={successModal} setOpen={handleSuccessModalClose}>
                <b>Dziękujemy za zainteresowanie. Wkrótce skontaktuje się z Tobą przedstawiciel wybranego przez Ciebie dealera.</b>
            </Modal>

            <Modal open={failureModal} setOpen={setFailureModal}>
                Wystąpił błąd!<br/>
                Prosimy spróbować później.
            </Modal>
        </>
    );
}